/**
 * Location Section Component
 *
 * React implementation of the location section renderer.
 * Supports variants: full, minimal, multi
 */

import { LocationData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface LocationProps extends SectionProps<LocationData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Location({ data, ctx, sectionId, elements }: LocationProps) {
  const variant = data.variant || 'full';

  switch (variant) {
    case 'minimal':
      return <LocationMinimal data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'multi':
      return <LocationMulti data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <LocationFull data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// FULL VARIANT
// =============================================================================

function LocationFull({ data, ctx, elements }: LocationProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="location"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map */}
          <div
            className="rounded-xl overflow-hidden min-h-[400px]"
            style={{ borderRadius: recipe.borders.cardRadius }}
          >
            {data.mapEmbedUrl ? (
              <iframe
                src={data.mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Location Map"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: recipe.colors.background }}
              >
                <p style={{ color: recipe.colors.textMuted }}>Map not available</p>
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {(data.eyebrow || eyebrowEl) && (
              <div className="mb-4">
                {eyebrowEl ? (
                  <EditableText
                    element={eyebrowEl}
                    as="span"
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  />
                ) : (
                  <span
                    className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: recipe.colors.primary }}
                  >
                    {data.eyebrow}
                  </span>
                )}
              </div>
            )}

            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              >
                {data.headline}
              </h2>
            ) : null}

            {subheadlineEl ? (
              <EditableText
                element={subheadlineEl}
                as="p"
                className="text-lg mb-8"
                style={{ color: recipe.colors.textMuted }}
              />
            ) : data.subheadline ? (
              <p className="text-lg mb-8" style={{ color: recipe.colors.textMuted }}>
                {data.subheadline}
              </p>
            ) : null}

            <div className="space-y-6">
              {/* Address */}
              {data.address && (
                <div className="flex gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>Address</p>
                    <p style={{ color: recipe.colors.textMuted }}>
                      {data.address.street}<br />
                      {data.address.city}, {data.address.state} {data.address.zip}
                    </p>
                    {data.directionsUrl && (
                      <a
                        href={data.directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium mt-1 inline-block"
                        style={{ color: recipe.colors.primary }}
                      >
                        Get Directions →
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Phone */}
              {data.phone && (
                <div className="flex gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>Phone</p>
                    <a
                      href={`tel:${data.phone}`}
                      style={{ color: recipe.colors.textMuted }}
                    >
                      {data.phone}
                    </a>
                  </div>
                </div>
              )}

              {/* Email */}
              {data.email && (
                <div className="flex gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>Email</p>
                    <a
                      href={`mailto:${data.email}`}
                      style={{ color: recipe.colors.textMuted }}
                    >
                      {data.email}
                    </a>
                  </div>
                </div>
              )}

              {/* Hours */}
              {data.hours && data.hours.length > 0 && (
                <div className="flex gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold mb-2" style={{ color: recipe.colors.text }}>Hours</p>
                    <div className="space-y-1">
                      {data.hours.map((h, i) => (
                        <div key={i} className="flex justify-between gap-8 text-sm">
                          <span style={{ color: recipe.colors.text }}>{h.day}</span>
                          <span style={{ color: h.isClosed ? recipe.colors.textMuted : recipe.colors.textMuted }}>
                            {h.isClosed ? 'Closed' : h.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// MINIMAL VARIANT
// =============================================================================

function LocationMinimal({ data, ctx, elements }: LocationProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="location"
      className="py-16"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h2"
            className="text-2xl md:text-3xl font-bold mb-6"
            style={{
              color: recipe.colors.text,
              fontFamily: `${recipe.typography.headingFont}, sans-serif`,
            }}
          />
        ) : data.headline ? (
          <h2
            className="text-2xl md:text-3xl font-bold mb-6"
            style={{
              color: recipe.colors.text,
              fontFamily: `${recipe.typography.headingFont}, sans-serif`,
            }}
          >
            {data.headline}
          </h2>
        ) : null}

        <div className="flex flex-wrap justify-center gap-8">
          {data.address && (
            <div>
              <p style={{ color: recipe.colors.textMuted }}>
                {data.address.street}, {data.address.city}, {data.address.state} {data.address.zip}
              </p>
            </div>
          )}
          {data.phone && (
            <a href={`tel:${data.phone}`} style={{ color: recipe.colors.primary }}>
              {data.phone}
            </a>
          )}
          {data.email && (
            <a href={`mailto:${data.email}`} style={{ color: recipe.colors.primary }}>
              {data.email}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// MULTI VARIANT
// =============================================================================

function LocationMulti({ data, ctx, elements }: LocationProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="location"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}
        </div>

        {/* Locations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.locations?.map((location, i) => (
            <div
              key={i}
              className="p-6 rounded-xl"
              style={{
                backgroundColor: recipe.colors.surface,
                borderRadius: recipe.borders.cardRadius,
                boxShadow: recipe.shadows.card,
              }}
            >
              <h3 className="text-xl font-semibold mb-4" style={{ color: recipe.colors.text }}>
                {location.name}
              </h3>

              {location.address && (
                <p className="mb-3" style={{ color: recipe.colors.textMuted }}>
                  {location.address.street}<br />
                  {location.address.city}, {location.address.state} {location.address.zip}
                </p>
              )}

              {location.phone && (
                <a
                  href={`tel:${location.phone}`}
                  className="block mb-3"
                  style={{ color: recipe.colors.primary }}
                >
                  {location.phone}
                </a>
              )}

              {location.hours && location.hours.length > 0 && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: `${recipe.colors.textMuted}20` }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: recipe.colors.text }}>Hours</p>
                  <div className="space-y-1">
                    {location.hours.map((h, hi) => (
                      <div key={hi} className="flex justify-between text-sm">
                        <span style={{ color: recipe.colors.textMuted }}>{h.day}</span>
                        <span style={{ color: recipe.colors.textMuted }}>{h.hours}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Location;
